import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import {
  Select, SelectContent, SelectGroup, SelectItem,
  SelectLabel, SelectSeparator, SelectTrigger, SelectValue,
} from './ui/select';
import { formatCurrency, formatDate, getHighLevelCategory } from '../utils/formatters';

interface ManualAccount {
  id: number;
  name: string;
  institution: string;
  category: string;
  type: 'asset' | 'liability';
  account_type: 'personal' | 'business';
  current_value?: number;
  currency: string;
}

interface MatchSuggestion {
  manual_account: ManualAccount;
  score: number;
  confidence: 'high' | 'medium' | 'low';
  reasons: string[];
  /** Latest transaction date (YYYY-MM-DD) already stored in Wealthini for this manual account */
  latest_local_transaction_date?: string | null;
}

interface PlaidAccount {
  account_id: string;
  name: string;
  mask: string;
  type: string;
  subtype: string;
  balance: number;
  currency: string;
}

interface AccountSuggestion {
  plaid_account: PlaidAccount;
  suggested_matches: MatchSuggestion[];
  action: 'link' | 'create_new';
}

// 'skip' = don't import; null = create new (personal); 'new_business' = create new (business); number = link to manual account id
export type AccountMapping = number | null | 'skip' | 'new_business';

/** How far back Plaid transactions are imported on the first sync for this linked account */
export type PlaidTransactionImportMode = 'full' | 'after_existing' | 'from_link_date';

interface AccountMatchingModalProps {
  isOpen: boolean;
  onClose: () => void;
  suggestions: AccountSuggestion[];
  onConfirm: (
    mappings: { [plaidAccountId: string]: AccountMapping },
    transactionImport: { [plaidAccountId: string]: PlaidTransactionImportMode }
  ) => void;
  loading?: boolean;
  hasBusinessAccounts?: boolean;
  /** Short disclaimer from API about typical Plaid history depth */
  plaidHistoryNote?: string | null;
  /** Latest transaction YYYY-MM-DD per manual account id (from API); preferred over per-row match fields */
  latestTransactionByManualAccount?: Record<string, string | null>;
}

// Encode/decode the select string value ↔ AccountMapping
const toSelectValue = (m: AccountMapping): string => {
  if (m === 'skip') return 'skip';
  if (m === null) return 'new';
  if (m === 'new_business') return 'new_business';
  return String(m);
};

const fromSelectValue = (v: string): AccountMapping => {
  if (v === 'skip') return 'skip';
  if (v === 'new') return null;
  if (v === 'new_business') return 'new_business';
  return parseInt(v, 10);
};

function latestForManualAccount(
  mapping: AccountMapping,
  latestTransactionByManualAccount: Record<string, string | null | undefined> | undefined,
  allSuggestions: AccountSuggestion[],
  currentRow: AccountSuggestion
): string | null {
  if (typeof mapping !== 'number' || Number.isNaN(mapping)) return null;
  const key = String(mapping);
  const fromApi = latestTransactionByManualAccount?.[key];
  if (fromApi != null && String(fromApi).trim() !== '') {
    return String(fromApi).slice(0, 10);
  }
  const sm = currentRow.suggested_matches.find(x => x.manual_account.id === mapping);
  if (sm?.latest_local_transaction_date) return sm.latest_local_transaction_date;
  for (const row of allSuggestions) {
    for (const m of row.suggested_matches) {
      if (m.manual_account.id === mapping && m.latest_local_transaction_date) {
        return m.latest_local_transaction_date;
      }
    }
  }
  return null;
}

function nextDayIsoDate(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + 1);
  return dt.toISOString().slice(0, 10);
}

const toHistorySelectValue = (mode: PlaidTransactionImportMode): string => mode;

const fromHistorySelectValue = (v: string): PlaidTransactionImportMode => {
  if (v === 'full' || v === 'after_existing' || v === 'from_link_date') return v;
  return 'full';
};

const AccountMatchingModal: React.FC<AccountMatchingModalProps> = ({
  isOpen,
  onClose,
  suggestions,
  onConfirm,
  loading = false,
  hasBusinessAccounts = false,
  plaidHistoryNote = null,
  latestTransactionByManualAccount = {},
}) => {
  const [mappings, setMappings] = useState<{ [plaidAccountId: string]: AccountMapping }>({});
  const [historyByPlaid, setHistoryByPlaid] = useState<{
    [plaidAccountId: string]: PlaidTransactionImportMode;
  }>({});

  useEffect(() => {
    if (!isOpen) {
      setHistoryByPlaid({});
      return;
    }
    const initial: { [id: string]: AccountMapping } = {};
    suggestions.forEach(({ plaid_account, suggested_matches }) => {
      const top = suggested_matches[0];
      initial[plaid_account.account_id] = top?.confidence === 'high' ? top.manual_account.id : null;
    });
    setMappings(initial);
    setHistoryByPlaid({});
  }, [isOpen, suggestions]);

  const handleChange = (plaidAccountId: string, value: string) => {
    const mapping = fromSelectValue(value);
    if (typeof mapping === 'number') {
      const conflict = Object.entries(mappings).find(
        ([id, m]) => id !== plaidAccountId && m === mapping
      );
      if (conflict) {
        const conflictName = suggestions.find(
          s => s.plaid_account.account_id === conflict[0]
        )?.plaid_account.name;
        const manualName = suggestions
          .flatMap(s => s.suggested_matches)
          .find(m => m.manual_account.id === mapping)?.manual_account.name;
        alert(
          `"${conflictName}" is already linked to "${manualName}". Each existing account can only be linked to one bank account.`
        );
        return;
      }
    }
    setMappings(prev => ({ ...prev, [plaidAccountId]: mapping }));
    setHistoryByPlaid(prev => {
      const next = { ...prev };
      delete next[plaidAccountId];
      return next;
    });
  };

  const handleHistoryChange = (plaidAccountId: string, value: string) => {
    setHistoryByPlaid(prev => ({ ...prev, [plaidAccountId]: fromHistorySelectValue(value) }));
  };

  const effectiveHistoryMode = (
    row: AccountSuggestion,
    mapping: AccountMapping
  ): PlaidTransactionImportMode => {
    if (mapping === 'skip') return 'full';
    if (mapping === null || mapping === 'new_business') return 'full';
    const latest = latestForManualAccount(
      mapping,
      latestTransactionByManualAccount,
      suggestions,
      row
    );
    const fallback: PlaidTransactionImportMode = latest ? 'after_existing' : 'full';
    return historyByPlaid[row.plaid_account.account_id] ?? fallback;
  };

  const toImport = Object.values(mappings).filter(v => v !== 'skip').length;
  const toLink = Object.values(mappings).filter(v => typeof v === 'number').length;
  const toCreate = Object.values(mappings).filter(v => v === null || v === 'new_business').length;
  const toSkip = Object.values(mappings).filter(v => v === 'skip').length;

  const claimedIds = new Set(
    Object.values(mappings).filter((v): v is number => typeof v === 'number')
  );

  const handleConfirm = () => {
    const transactionImport: { [plaidAccountId: string]: PlaidTransactionImportMode } = {};
    for (const row of suggestions) {
      const id = row.plaid_account.account_id;
      const m = mappings[id];
      if (m === 'skip') continue;
      transactionImport[id] = effectiveHistoryMode(row, m);
    }
    onConfirm(mappings, transactionImport);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Connect your accounts</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-[var(--text-secondary)] -mt-1 mb-2">
          Choose what to do with each account Plaid found. Link it to an existing Wealthini
          account, import it as new, or skip it.
        </p>
        {plaidHistoryNote ? (
          <p className="text-xs text-[var(--text-muted)] mb-4">{plaidHistoryNote}</p>
        ) : null}

        <div className="space-y-3">
          {suggestions.map(row => {
            const { plaid_account, suggested_matches } = row;
            const current = mappings[plaid_account.account_id];
            const mapping = current;
            const latest =
              typeof mapping === 'number'
                ? latestForManualAccount(
                    mapping,
                    latestTransactionByManualAccount,
                    suggestions,
                    row
                  )
                : null;
            const historyMode = effectiveHistoryMode(row, mapping);
            const firstImportDay = latest ? nextDayIsoDate(latest) : null;

            return (
              <div
                key={plaid_account.account_id}
                className="rounded-xl border border-[var(--border-color)] px-4 py-3 space-y-3"
              >
                <div className="grid grid-cols-[1fr_auto] gap-3 items-center">
                  <div className="min-w-0">
                    <p className="font-medium text-[var(--text-primary)] truncate">
                      {plaid_account.name}
                    </p>
                    <p className="text-xs text-[var(--text-muted)]">
                      ···· {plaid_account.mask}
                      {' · '}
                      <span className="capitalize">{plaid_account.subtype}</span>
                      {' · '}
                      {formatCurrency(plaid_account.balance, plaid_account.currency)}
                    </p>
                  </div>

                  <Select
                    value={toSelectValue(current)}
                    onValueChange={v => handleChange(plaid_account.account_id, v)}
                  >
                    <SelectTrigger className="w-56 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {suggested_matches.length > 0 && (
                        <>
                          <SelectGroup>
                            <SelectLabel>Link to existing</SelectLabel>
                            {suggested_matches.map(match => {
                              const alreadyClaimed =
                                claimedIds.has(match.manual_account.id) &&
                                current !== match.manual_account.id;
                              const isBusiness = match.manual_account.account_type === 'business';
                              return (
                                <SelectItem
                                  key={match.manual_account.id}
                                  value={String(match.manual_account.id)}
                                  disabled={alreadyClaimed}
                                >
                                  {match.manual_account.name}
                                  {isBusiness ? ' · Business' : ''}
                                  {match.confidence === 'high' ? ' ✓' : ''}
                                </SelectItem>
                              );
                            })}
                          </SelectGroup>
                          <SelectSeparator />
                        </>
                      )}

                      <SelectGroup>
                        <SelectLabel>Or</SelectLabel>
                        <SelectItem value="new">
                          Import as new
                          {hasBusinessAccounts
                            ? ' — Personal'
                            : ` — ${getHighLevelCategory(plaid_account.subtype || '')}`}
                        </SelectItem>
                        {hasBusinessAccounts && (
                          <SelectItem value="new_business">
                            Import as new — Business
                          </SelectItem>
                        )}
                        <SelectItem value="skip">Don&apos;t import</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>

                {mapping !== 'skip' && (
                  <div className="pt-2 border-t border-[var(--border-color)] space-y-2">
                    <p className="text-xs font-medium text-[var(--text-secondary)]">
                      Transaction history from your bank
                    </p>
                    {mapping === null || mapping === 'new_business' ? (
                      <p className="text-xs text-[var(--text-muted)]">
                        We&apos;ll import as much history as your bank shares through Plaid
                        (often on the order of two years for new accounts).
                      </p>
                    ) : (
                      <>
                        {latest ? (
                          <p className="text-xs text-[var(--text-muted)]">
                            Latest transaction we see on this Wealthini account:{' '}
                            <span className="text-[var(--text-secondary)]">
                              {formatDate(latest)}
                            </span>
                            . The next calendar day is the first date we&apos;ll take from
                            Plaid if you choose &quot;after last&quot; below.
                          </p>
                        ) : (
                          <p className="text-xs text-[var(--text-muted)]">
                            We couldn&apos;t find a transaction date on this Wealthini account
                            (for example if data lives on another account, or the link path
                            differs). You can still pick &quot;after last Wealthini
                            transaction&quot;; until we have a last date on file it behaves
                            like full history. Use full history if you want everything Plaid
                            offers regardless.
                          </p>
                        )}
                        <Select
                          value={toHistorySelectValue(historyMode)}
                          onValueChange={v =>
                            handleHistoryChange(plaid_account.account_id, v)
                          }
                        >
                          <SelectTrigger className="w-full text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="after_existing">
                              {latest && firstImportDay
                                ? `From ${formatDate(firstImportDay)} onward (after ${formatDate(latest)} in Wealthini)`
                                : 'From day after last Wealthini transaction (no last date on file — same as full history for now)'}
                            </SelectItem>
                            <SelectItem value="full">
                              Import full history from Plaid (may duplicate older entries)
                            </SelectItem>
                            <SelectItem value="from_link_date">
                              Only from the link date onward (skip older Plaid activity)
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        {historyMode === 'after_existing' && latest && firstImportDay ? (
                          <p className="text-xs text-[var(--text-muted)]">
                            Import starts on{' '}
                            <span className="text-[var(--text-secondary)]">
                              {formatDate(firstImportDay)}
                            </span>{' '}
                            (first calendar day after your latest Wealthini transaction on{' '}
                            {formatDate(latest)}).
                          </p>
                        ) : null}
                        {historyMode === 'after_existing' && !latest ? (
                          <p className="text-xs text-[var(--text-muted)]">
                            No cutoff date on file yet — this run will import the same range
                            as full Plaid history until we can determine your last Wealthini
                            date.
                          </p>
                        ) : null}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between pt-4 mt-2 border-t border-[var(--border-color)]">
          <p className="text-xs text-[var(--text-muted)]">
            {[
              toLink > 0 && `${toLink} linked`,
              toCreate > 0 && `${toCreate} new`,
              toSkip > 0 && `${toSkip} skipped`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          <div className="flex gap-3">
            <Button variant="ghost" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleConfirm}
              disabled={loading || toImport === 0}
            >
              {loading
                ? 'Importing...'
                : toImport === 0
                  ? 'Select at least one'
                  : `Import ${toImport} account${toImport !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AccountMatchingModal;
